import { AppPlatform, AppStoreType } from '@/types';
import { Destination } from '@/types/destinations';
import { displayTextForAppPlatform } from '@/helpers/displayHelpers';
import { getIsApplePlatform, isGooglePlatform } from '@/helpers';

export const getDestinationId = (destination: Destination): string =>
    destination.destinationId ?? destination.stepConfigId;

export const isAndroidDependenciesDestination = (destination: Destination): boolean =>
    isGooglePlatform(destination.platform) || destination.platform === AppPlatform.AndroidSDK;

export const isIosDependenciesDestination = (destination: Destination): boolean =>
    getIsApplePlatform(destination.platform) || destination.platform === AppPlatform.IosSDK;

interface DestinationHeaderInput {
    destinationId: string;
    destinationName?: string;
    platform?: AppPlatform;
    appStoreType?: AppStoreType;
}

export const buildDestinationHeaderLabels = (destinations: DestinationHeaderInput[]): Record<string, string> => {
    // Unnamed destinations are labelled by platform, which is short and meaningful; the id is only
    // added below when two would otherwise be indistinguishable.
    const baseLabelFor = (d: DestinationHeaderInput): string => {
        const name = d.destinationName?.trim();
        if (name) return name;
        return d.platform ? displayTextForAppPlatform(d.platform, d.appStoreType) : d.destinationId;
    };
    const baseCounts = new Map<string, number>();
    destinations.forEach((d) => {
        const base = baseLabelFor(d);
        baseCounts.set(base, (baseCounts.get(base) ?? 0) + 1);
    });
    const result: Record<string, string> = {};
    destinations.forEach((d) => {
        const base = baseLabelFor(d);
        result[d.destinationId] = (baseCounts.get(base) ?? 0) > 1 ? `${base} (${d.destinationId})` : base;
    });
    return result;
};

export const compareVersionStrings = (a?: string, b?: string): number => {
    const aTrim = (a ?? '').trim();
    const bTrim = (b ?? '').trim();
    if (!aTrim && !bTrim) return 0;
    if (!aTrim) return 1;
    if (!bTrim) return -1;
    const aParts = aTrim
        .replace(/^v/, '')
        .split('.')
        .map((p) => Number.parseInt(p, 10));
    const bParts = bTrim
        .replace(/^v/, '')
        .split('.')
        .map((p) => Number.parseInt(p, 10));
    const len = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < len; i++) {
        const aVal = Number.isFinite(aParts[i]) ? aParts[i] : 0;
        const bVal = Number.isFinite(bParts[i]) ? bParts[i] : 0;
        if (aVal !== bVal) {
            return aVal - bVal;
        }
    }
    return aTrim.localeCompare(bTrim);
};

export const pluralize = (n: number, singular: string, plural: string): string => (n === 1 ? singular : plural);
